/**
 * Luqta SDK Client
 *
 * Main client class for the Luqta SDK supporting two modes:
 * 1. Custom UI Mode - API-only integration for developers building their own UI
 * 2. Pre-configured UI Mode - Complete UI with customizable branding
 *
 * And two API access types:
 * 1. Application APIs - Only require API Key + App ID (public data)
 * 2. End-User APIs - Require API Key + App ID + User identification
 */

import {
  LuqtaConfig,
  CustomModeConfig,
  PreconfiguredModeConfig,
  LegacyConfig,
  RequestOptions,
  ApiResponse,
  LuqtaError,
  UserProfile,
  UserIdentification,
  BrandingConfig,
  UIConfig,
  APPLICATION_ENDPOINTS,
  USER_ENDPOINTS,
  Contest,
  PaginatedResponse,
  UIAction,
} from './types';
import { LuqtaUIRenderer } from './ui/renderer';

// ============================================================================
// LUQTA CLIENT CLASS
// ============================================================================

/**
 * Main Luqta SDK Client
 *
 * Supports two modes of operation:
 * - Custom Mode: API-only integration for custom UI implementations
 * - Pre-configured Mode: Complete UI with branding customization
 *
 * And two API authentication types:
 * - Application APIs: Only need apiKey + appId (for public data like contest listings)
 * - End-User APIs: Need apiKey + appId + user identification (for user-specific actions)
 *
 * @example Custom Mode with End-User APIs:
 * ```typescript
 * const client = new LuqtaClient({
 *   mode: 'custom',
 *   apiKey: 'your-api-key',
 *   appId: 'your-app-id',
 *   user: { email: 'user@example.com' }
 * });
 *
 * await client.initializeUser();
 * const contests = await client.contests.getAll();
 * await client.contests.participate(123);
 * ```
 *
 * @example Custom Mode with Application-Only APIs:
 * ```typescript
 * // No user required for public data
 * const client = new LuqtaClient({
 *   mode: 'custom',
 *   apiKey: 'your-api-key',
 *   appId: 'your-app-id'
 * });
 *
 * // These work without user initialization
 * const contests = await client.getPublicContests();
 * const categories = await client.getCategories();
 * ```
 *
 * @example Pre-configured Mode (Complete UI):
 * ```typescript
 * const client = new LuqtaClient({
 *   mode: 'preconfigured',
 *   apiKey: 'your-api-key',
 *   appId: 'your-app-id',
 *   containerId: 'luqta-container',
 *   user: { email: 'user@example.com' },
 *   branding: {
 *     primaryColor: '#5304fb',
 *     logoUrl: 'https://example.com/logo.png'
 *   }
 * });
 *
 * await client.render();
 * ```
 */
export class LuqtaClient {
  // Configuration
  private apiKey: string;
  private appId: string;
  private baseURL: string;
  private timeout: number;
  private production: boolean;
  private defaultHeaders: Record<string, string>;

  // Mode & User
  private mode: 'custom' | 'preconfigured';
  private user?: UserIdentification;
  private isUserInitialized: boolean = false;

  // Pre-configured mode options
  private containerId?: string;
  private branding?: BrandingConfig;
  private locale: 'en' | 'ar' = 'en';
  private rtl: boolean = false;
  private screens: string[] = ['contests', 'quizzes', 'rewards', 'profile'];
  private onAction?: (action: any) => void;
  private onError?: (error: LuqtaError) => void;
  private showProfileIcon: boolean = false;

  // SDK initialization state
  private isSdkInitialized: boolean = false;
  private zeroStorage: boolean = false;
  private userProfile: UserProfile | undefined = undefined;

  // Constants
  private readonly SDK_AUTH_TOKEN_KEY = 'clientAuthorizationAccessTokenLuqtaSdk';
  private readonly USER_AUTH_TOKEN_KEY = 'userAuthorizationAccessTokenLuqtaSdk';
  private readonly EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  private readonly PHONE_PATTERN = /^\+[1-9]\d{1,14}$/;

  // ============================================================================
  // SECURITY: Rate Limiting & Anti-Abuse Protection
  // ============================================================================

  // Rate limiting configuration
  private readonly RATE_LIMIT_WINDOW_MS = 60000; // 1 minute window
  private readonly MAX_REQUESTS_PER_WINDOW = 60; // Max 60 requests per minute
  private readonly MAX_REQUESTS_PER_ENDPOINT = 30; // Max 30 requests per endpoint per minute
  private readonly BURST_LIMIT = 10; // Max 10 requests in 1 second
  private readonly BURST_WINDOW_MS = 1000;
  private readonly LOCKOUT_DURATION_MS = 300000; // 5 minutes lockout after abuse
  private readonly MAX_VIOLATIONS_BEFORE_LOCKOUT = 3;

  // Rate limiting state
  private requestTimestamps: number[] = [];
  private endpointRequestCounts: Map<string, { count: number; resetTime: number }> = new Map();
  private burstTimestamps: number[] = [];
  private violationCount: number = 0;
  private lockoutUntil: number = 0;

  // Request deduplication
  private readonly DEDUP_WINDOW_MS = 500; // Dedupe identical requests within 500ms
  private recentRequests: Map<string, number> = new Map();

  // Token integrity
  private readonly TOKEN_FINGERPRINT_KEY = 'luqta_sdk_fingerprint';
  private sessionFingerprint: string = '';

  // Security event logging
  private securityEvents: Array<{ type: string; timestamp: number; details: string }> = [];
  private readonly MAX_SECURITY_EVENTS = 100;

  // Sub-clients for organized API access
  public readonly contests: ContestAPI;
  public readonly levels: LevelAPI;
  public readonly quiz: QuizAPI;
  public readonly rewards: RewardAPI;
  public readonly notifications: NotificationAPI;
  public readonly profile: ProfileAPI;

  // UI Renderer (for preconfigured mode)
  private uiRenderer?: LuqtaUIRenderer;

  /**
   * Creates a new LuqtaClient instance
   */
  constructor(config: LuqtaConfig | LegacyConfig) {
    // Handle legacy config format
    const normalizedConfig = this.normalizeConfig(config);

    this.validateBaseConfig(normalizedConfig);

    this.apiKey = normalizedConfig.apiKey;
    this.appId = normalizedConfig.appId;
    this.production = normalizedConfig.production ?? false;
    this.timeout = normalizedConfig.timeout ?? 30000;
    this.baseURL = normalizedConfig.baseURL || this.constructBaseURL(this.production);
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...normalizedConfig.headers
    };

    // Set mode
    this.mode = 'mode' in normalizedConfig ? normalizedConfig.mode : 'custom';
    this.zeroStorage = normalizedConfig.zeroStorage ?? false;
    this.userProfile = normalizedConfig.userProfile;

    // Set user if provided
    if ('user' in normalizedConfig && normalizedConfig.user) {
      this.user = normalizedConfig.user;
      this.validateUserConfig(this.user);
    }

    // Pre-configured mode settings
    if (this.mode === 'preconfigured' && 'containerId' in normalizedConfig) {
      const preconfigured = normalizedConfig as PreconfiguredModeConfig;
      this.containerId = preconfigured.containerId;
      this.branding = preconfigured.branding;
      this.locale = preconfigured.locale ?? 'en';
      this.rtl = preconfigured.rtl ?? false;
      this.screens = preconfigured.screens ?? ['contests', 'quizzes', 'rewards', 'profile'];
      this.onAction = preconfigured.onAction;
      this.onError = preconfigured.onError;
      this.showProfileIcon = (preconfigured as any).showProfileIcon ?? false;
    }

    // Initialize sub-clients
    this.contests = new ContestAPI(this);
    this.levels = new LevelAPI(this);
    this.quiz = new QuizAPI(this);
    this.rewards = new RewardAPI(this);
    this.notifications = new NotificationAPI(this);
    this.profile = new ProfileAPI(this);

    // Initialize security features
    this.initializeSecurity();

    // Rehydrate init flags from persisted cookies so a returning user's
    // stored tokens are picked up without explicit re-init.
    this.tryRestoreSession();
  }

  /**
   * Normalizes legacy config to new format
   */
  private normalizeConfig(config: LuqtaConfig | LegacyConfig): LuqtaConfig {
    if ('mode' in config) {
      return config as LuqtaConfig;
    }

    // Legacy format - convert to custom mode
    return {
      mode: 'custom',
      apiKey: config.apiKey,
      appId: config.appId,
      production: config.production,
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: config.headers,
      user: config.user,
      zeroStorage: (config as any).zeroStorage
    } as CustomModeConfig;
  }

  /**
   * Validates base configuration
   */
  private validateBaseConfig(config: LuqtaConfig): void {
    if (!config.apiKey) {
      throw new LuqtaError('API key is required', 'MISSING_API_KEY');
    }

    if (!config.appId) {
      throw new LuqtaError('Application ID is required', 'MISSING_APP_ID');
    }

    if (config.mode === 'preconfigured' && 'containerId' in config && !config.containerId) {
      throw new LuqtaError('Container ID is required for preconfigured mode', 'MISSING_CONTAINER_ID');
    }
  }

  /**
   * Validates user configuration
   * At least one of email, phone_number, username, or uuid is required
   */
  private validateUserConfig(user: UserIdentification): void {
    const email    = user.email?.trim().toLowerCase();
    const phone    = user.phone_number?.trim();
    const username = user.username?.trim();
    const uuid     = user.uuid?.trim();

    const hasEmail    = !!email;
    const hasPhone    = !!phone;
    const hasUsername = !!username;
    const hasUuid     = !!uuid;

    if (!hasEmail && !hasPhone && !hasUsername && !hasUuid) {
      throw new LuqtaError(
        'At least one identifier is required: email, phone_number, username, or uuid',
        'MISSING_USER_INFO'
      );
    }

    if (hasEmail && !this.EMAIL_PATTERN.test(email!)) {
      throw new LuqtaError(
        'Invalid email format. Please provide a valid email address',
        'INVALID_EMAIL_FORMAT'
      );
    }

    if (hasPhone && !this.PHONE_PATTERN.test(phone!)) {
      throw new LuqtaError(
        'Invalid phone number format. Use international format (e.g., +923001234567)',
        'INVALID_PHONE_FORMAT'
      );
    }
  }

  /**
   * Constructs base URL based on environment
   */
  private constructBaseURL(isProduction: boolean): string {
    const envSuffix = isProduction ? '' : ':dev';
    return `https://xi5f-5y62-kbyr.n7e.xano.io/api:ev5HkJDh${envSuffix}`;
  }

  // ============================================================================
  // SECURITY METHODS: Rate Limiting, Validation & Protection
  // ============================================================================

  /**
   * Generates a unique session fingerprint for token integrity
   */
  private generateSessionFingerprint(): string {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      this.appId
    ];
    // Simple hash function
    let hash = 0;
    const str = components.join('|');
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Initializes security features
   */
  private initializeSecurity(): void {
    this.sessionFingerprint = this.generateSessionFingerprint();
    this.cleanupOldSecurityData();
  }

  /**
   * Logs security events for monitoring
   */
  private logSecurityEvent(type: string, details: string): void {
    const event = {
      type,
      timestamp: Date.now(),
      details
    };

    this.securityEvents.push(event);

    // Keep only recent events
    if (this.securityEvents.length > this.MAX_SECURITY_EVENTS) {
      this.securityEvents = this.securityEvents.slice(-this.MAX_SECURITY_EVENTS);
    }

    // Log to console in development
    if (!this.production) {
      console.warn(`[Luqta Security] ${type}: ${details}`);
    }
  }

  /**
   * Checks if client is currently locked out due to abuse
   */
  private isLockedOut(): boolean {
    if (Date.now() < this.lockoutUntil) {
      return true;
    }
    // Reset lockout
    if (this.lockoutUntil > 0 && Date.now() >= this.lockoutUntil) {
      this.lockoutUntil = 0;
      this.violationCount = 0;
    }
    return false;
  }

  /**
   * Records a security violation and potentially locks out the client
   */
  private recordViolation(reason: string): void {
    this.violationCount++;
    this.logSecurityEvent('VIOLATION', `${reason} (Count: ${this.violationCount})`);

    if (this.violationCount >= this.MAX_VIOLATIONS_BEFORE_LOCKOUT) {
      this.lockoutUntil = Date.now() + this.LOCKOUT_DURATION_MS;
      this.logSecurityEvent('LOCKOUT', `Client locked out for ${this.LOCKOUT_DURATION_MS / 1000} seconds`);
    }
  }

  /**
   * Cleans up old timestamps for rate limiting
   */
  private cleanupOldSecurityData(): void {
    const now = Date.now();

    // Clean up request timestamps
    this.requestTimestamps = this.requestTimestamps.filter(
      ts => now - ts < this.RATE_LIMIT_WINDOW_MS
    );

    // Clean up burst timestamps
    this.burstTimestamps = this.burstTimestamps.filter(
      ts => now - ts < this.BURST_WINDOW_MS
    );

    // Clean up endpoint counts
    this.endpointRequestCounts.forEach((value, key) => {
      if (now > value.resetTime) {
        this.endpointRequestCounts.delete(key);
      }
    });

    // Clean up recent requests for deduplication
    this.recentRequests.forEach((timestamp, key) => {
      if (now - timestamp > this.DEDUP_WINDOW_MS) {
        this.recentRequests.delete(key);
      }
    });
  }

  /**
   * Checks rate limits before making a request
   * @throws {LuqtaError} If rate limit exceeded
   */
  private checkRateLimit(endpoint: string): void {
    const now = Date.now();
    this.cleanupOldSecurityData();

    // Check if locked out
    if (this.isLockedOut()) {
      const remainingTime = Math.ceil((this.lockoutUntil - now) / 1000);
      throw new LuqtaError(
        `Too many requests. Please wait ${remainingTime} seconds before trying again.`,
        'RATE_LIMIT_LOCKOUT'
      );
    }

    // Check burst limit (requests per second)
    if (this.burstTimestamps.length >= this.BURST_LIMIT) {
      this.recordViolation('Burst limit exceeded');
      throw new LuqtaError(
        'Too many requests in a short time. Please slow down.',
        'BURST_LIMIT_EXCEEDED'
      );
    }

    // Check overall rate limit
    if (this.requestTimestamps.length >= this.MAX_REQUESTS_PER_WINDOW) {
      this.recordViolation('Overall rate limit exceeded');
      throw new LuqtaError(
        'Rate limit exceeded. Please wait before making more requests.',
        'RATE_LIMIT_EXCEEDED'
      );
    }

    // Check per-endpoint limit
    const endpointKey = endpoint.split('?')[0]; // Remove query params
    const endpointCount = this.endpointRequestCounts.get(endpointKey);

    if (endpointCount && endpointCount.count >= this.MAX_REQUESTS_PER_ENDPOINT) {
      this.recordViolation(`Endpoint rate limit exceeded for ${endpointKey}`);
      throw new LuqtaError(
        'Too many requests to this endpoint. Please wait.',
        'ENDPOINT_RATE_LIMIT_EXCEEDED'
      );
    }

    // Record the request
    this.requestTimestamps.push(now);
    this.burstTimestamps.push(now);

    // Update endpoint count
    if (endpointCount) {
      endpointCount.count++;
    } else {
      this.endpointRequestCounts.set(endpointKey, {
        count: 1,
        resetTime: now + this.RATE_LIMIT_WINDOW_MS
      });
    }
  }

  /**
   * Checks for duplicate requests (prevents accidental double submissions)
   * @returns true if request is a duplicate
   */
  private isDuplicateRequest(endpoint: string, body?: any): boolean {
    const requestKey = `${endpoint}:${JSON.stringify(body || '')}`;
    const lastRequestTime = this.recentRequests.get(requestKey);

    if (lastRequestTime && Date.now() - lastRequestTime < this.DEDUP_WINDOW_MS) {
      this.logSecurityEvent('DUPLICATE_REQUEST', `Blocked duplicate request to ${endpoint}`);
      return true;
    }

    this.recentRequests.set(requestKey, Date.now());
    return false;
  }

  /**
   * Validates and sanitizes request payload
   * Prevents injection attacks and malformed data
   */
  private sanitizePayload(payload: any): any {
    if (payload === null || payload === undefined) {
      return payload;
    }

    if (typeof payload === 'string') {
      // Remove potential XSS vectors and trim
      return payload
        .trim()
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+=/gi, '');
    }

    if (Array.isArray(payload)) {
      // Limit array size to prevent DoS
      if (payload.length > 1000) {
        this.logSecurityEvent('PAYLOAD_VALIDATION', 'Array size exceeded limit');
        throw new LuqtaError('Payload too large', 'PAYLOAD_TOO_LARGE');
      }
      return payload.map(item => this.sanitizePayload(item));
    }

    if (typeof payload === 'object') {
      // Limit object depth and size
      const keys = Object.keys(payload);
      if (keys.length > 100) {
        this.logSecurityEvent('PAYLOAD_VALIDATION', 'Object key count exceeded limit');
        throw new LuqtaError('Payload too complex', 'PAYLOAD_TOO_COMPLEX');
      }

      const sanitized: any = {};
      for (const key of keys) {
        // Sanitize key names
        const sanitizedKey = key.replace(/[<>'"]/g, '').trim();
        if (sanitizedKey && sanitizedKey.length <= 100) {
          sanitized[sanitizedKey] = this.sanitizePayload(payload[key]);
        }
      }
      return sanitized;
    }

    // Numbers, booleans, etc. pass through
    return payload;
  }

  /**
   * Validates token integrity
   * Checks if token has been tampered with
   * Supports both JWT (3 parts) and JWE (5 parts) token formats
   */
  private validateTokenIntegrity(token: string | null, tokenType: 'sdk' | 'user'): boolean {
    if (!token) return true; // No token to validate

    // Check token format
    // JWT format: header.payload.signature (3 parts)
    // JWE format: header.encryptedKey.iv.ciphertext.tag (5 parts)
    const parts = token.split('.');
    if (parts.length !== 3 && parts.length !== 5) {
      this.logSecurityEvent('TOKEN_VALIDATION', `Invalid ${tokenType} token format (expected 3 or 5 parts, got ${parts.length})`);
      return false;
    }

    // Check for reasonable token length (JWE tokens can be longer)
    if (token.length < 50 || token.length > 5000) {
      this.logSecurityEvent('TOKEN_VALIDATION', `Suspicious ${tokenType} token length: ${token.length}`);
      return false;
    }

    // Check for valid base64url characters in each part
    // Base64url allows: A-Z, a-z, 0-9, -, _ (no padding = required in middle parts)
    const base64urlPattern = /^[A-Za-z0-9_-]*$/;
    for (let i = 0; i < parts.length; i++) {
      if (!base64urlPattern.test(parts[i])) {
        this.logSecurityEvent('TOKEN_VALIDATION', `${tokenType} token part ${i + 1} contains invalid characters`);
        return false;
      }
    }

    return true;
  }

  /**
   * Validates numeric parameters to prevent injection
   */
  private validateNumericParam(value: any, paramName: string): number {
    const num = Number(value);
    if (isNaN(num) || !isFinite(num)) {
      throw new LuqtaError(
        `Invalid ${paramName}: must be a valid number`,
        'INVALID_PARAMETER'
      );
    }
    // Prevent extremely large numbers
    if (Math.abs(num) > Number.MAX_SAFE_INTEGER) {
      throw new LuqtaError(
        `Invalid ${paramName}: value too large`,
        'PARAMETER_OUT_OF_RANGE'
      );
    }
    return num;
  }

  /**
   * Validates string parameters to prevent injection
   */
  private validateStringParam(value: any, paramName: string, maxLength: number = 500): string {
    if (typeof value !== 'string') {
      throw new LuqtaError(
        `Invalid ${paramName}: must be a string`,
        'INVALID_PARAMETER'
      );
    }

    const trimmed = value.trim();

    if (trimmed.length === 0) {
      throw new LuqtaError(
        `Invalid ${paramName}: cannot be empty`,
        'INVALID_PARAMETER'
      );
    }

    if (trimmed.length > maxLength) {
      throw new LuqtaError(
        `Invalid ${paramName}: exceeds maximum length of ${maxLength}`,
        'PARAMETER_TOO_LONG'
      );
    }

    return trimmed;
  }

  /**
   * Performs security checks before making an API request
   * This is the main security gate for all API calls
   */
  private performSecurityChecks(endpoint: string, options?: RequestOptions): void {
    // 1. Check rate limits
    this.checkRateLimit(endpoint);

    // 2. Check for duplicate requests (only for mutating operations)
    if (options?.method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method)) {
      if (this.isDuplicateRequest(endpoint, options.body)) {
        throw new LuqtaError(
          'Duplicate request detected. Please wait.',
          'DUPLICATE_REQUEST'
        );
      }
    }

    // 3. Validate tokens
    const sdkToken = this.getSdkAuthToken();
    const userToken = this.getUserAuthToken();

    if (sdkToken && !this.validateTokenIntegrity(sdkToken, 'sdk')) {
      // Clear corrupted token
      this.clearSdkAuthToken();
      throw new LuqtaError(
        'SDK token validation failed. Please re-initialize.',
        'TOKEN_VALIDATION_FAILED'
      );
    }

    if (userToken && !this.validateTokenIntegrity(userToken, 'user')) {
      // Clear corrupted token
      this.clearUserAuthToken();
      throw new LuqtaError(
        'User token validation failed. Please re-initialize user.',
        'TOKEN_VALIDATION_FAILED'
      );
    }

    // 4. Sanitize payload if present
    if (options?.body) {
      options.body = this.sanitizePayload(options.body);
    }

    // 5. Sanitize query parameters
    if (options?.params) {
      options.params = this.sanitizePayload(options.params);
    }
  }

  /**
   * Clears SDK auth token
   */
  private clearSdkAuthToken(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.SDK_AUTH_TOKEN_KEY);
      }
    } catch {
      // Storage not available
    }
  }

  /**
   * Clears user auth token
   */
  private clearUserAuthToken(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.USER_AUTH_TOKEN_KEY);
      }
    } catch {
      // Storage not available
    }
    this.isUserInitialized = false;
  }

  /**
   * Clears user auth token (call on logout)
   */
  public clearUserToken(): void {
    this.clearUserAuthToken();
  }

  /**
   * Gets security status for debugging/monitoring
   */
  public getSecurityStatus(): {
    isLockedOut: boolean;
    lockoutRemaining: number;
    violationCount: number;
    recentRequestCount: number;
    recentEvents: Array<{ type: string; timestamp: number; details: string }>;
  } {
    return {
      isLockedOut: this.isLockedOut(),
      lockoutRemaining: Math.max(0, this.lockoutUntil - Date.now()),
      violationCount: this.violationCount,
      recentRequestCount: this.requestTimestamps.length,
      recentEvents: this.securityEvents.slice(-10)
    };
  }

  /**
   * Resets security state (for testing purposes only)
   * WARNING: This should not be used in production
   */
  public resetSecurityState(): void {
    if (this.production) {
      this.logSecurityEvent('SECURITY_RESET_ATTEMPT', 'Attempted to reset security in production');
      return;
    }

    this.requestTimestamps = [];
    this.burstTimestamps = [];
    this.endpointRequestCounts.clear();
    this.recentRequests.clear();
    this.violationCount = 0;
    this.lockoutUntil = 0;
    this.securityEvents = [];
  }

  // ============================================================================
  // SDK INITIALIZATION (Application-level)
  // ============================================================================

  /**
   * Initializes the SDK with API key and App ID
   * This is automatically called and does NOT require any user information
   * Only requires apiKey and appId which are passed in the config
   *
   * After successful initialization, it fetches contests to validate SDK access
   *
   * @returns Promise with initialization result including contests
   * @throws {LuqtaError} If initialization fails
   */
  async initializeSdk(): Promise<{
    success: boolean;
    contests: PaginatedResponse<Contest>;
  }> {
    // Security: Check rate limits for initialization
    this.checkRateLimit('/sdk/app/initialize');

    const initURL = `${this.baseURL}/sdk/app/initialize`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.apiKey}`,
      'X-App-Token': this.appId,
      'X-Data-Source': this.production ? 'live' : 'dev',
    };
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(initURL, {
        method: 'POST',
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.parseErrorResponse(response, 'SDK initialization failed', 'SDK_INIT_FAILED');
      }

      const data = await response.json();

      if (!data.success) {
        throw new LuqtaError(
          data.message || 'SDK initialization failed',
          data.code || 'SDK_INIT_FAILED',
          undefined,
          data
        );
      }

      if (data.data?.authToken) {
        this.storeSdkAuthToken(data.data.authToken);
        this.isSdkInitialized = true;
      } else {
        throw new LuqtaError('No authentication token received from SDK initialization', 'MISSING_SDK_AUTH_TOKEN');
      }

      // After successful initialization, fetch contests
      const contestsResponse = await this.fetchContests({ page: 1, per_page: 10 });

      return {
        success: true,
        contests: contestsResponse
      };
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.handleError(error, 'SDK initialization');
    }
  }

  /**
   * Fetches contests with server-side pagination
   * Uses SDK auth token from initialization
   *
   * @param params - Pagination parameters (page, per_page)
   * @returns Paginated contests response
   * @throws {LuqtaError} If no contests exist or fetch fails
   */
  async fetchContests(params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<Contest>> {
    if (!this.isSdkInitialized) {
      throw new LuqtaError(
        'SDK must be initialized before fetching contests. Call initializeSdk() first.',
        'SDK_NOT_INITIALIZED'
      );
    }

    const sdkToken = this.getSdkAuthToken();
    if (!sdkToken) {
      throw new LuqtaError(
        'SDK authentication token not found. Please re-initialize the SDK.',
        'MISSING_SDK_TOKEN'
      );
    }

    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.per_page) queryParams.append('per_page', String(params.per_page));

    const url = `${this.baseURL}/sdk/app/contest/all${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sdkToken}`,
      'X-App-Token': this.appId,
      'X-Data-Source': this.production ? 'live' : 'dev',
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.parseErrorResponse(response, 'Failed to fetch contests', 'FETCH_CONTESTS_FAILED');
      }

      const data = await response.json();

      if (!data.success) {
        throw new LuqtaError(
          data.message || 'Failed to fetch contests',
          data.code || 'FETCH_CONTESTS_FAILED',
          undefined,
          data
        );
      }

      // Return response even if empty (UI will handle empty state)
      return data as PaginatedResponse<Contest>;
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.handleError(error, 'Fetch contests');
    }
  }

  /**
   * Checks if SDK is initialized
   */
  isSdkReady(): boolean {
    return this.isSdkInitialized;
  }

  // ============================================================================
  // UI CONFIGURATION
  // ============================================================================

  /**
   * Configures the SDK for pre-configured UI mode
   * Call this after creating the client to set up UI settings and branding
   *
   * @param config - UI configuration including containerId, branding, locale, etc.
   * @returns The client instance for method chaining
   *
   * @example
   * ```typescript
   * const client = new LuqtaClient({
   *   apiKey: 'lq_dev_app_xxx',
   *   appId: 'your-app-id'
   * });
   *
   * client.configure({
   *   containerId: 'luqta-container',
   *   branding: {
   *     primaryColor: '#5304fb',
   *     secondaryColor: '#8f67fd',
   *     backgroundColor: '#ffffff',
   *     textColor: '#111827',
   *     logoUrl: 'https://example.com/logo.png',
   *     appName: 'My App',
   *     borderRadius: 8,
   *     fontFamily: 'Inter, sans-serif'
   *   },
   *   locale: 'en',
   *   rtl: false,
   *   screens: ['contests', 'quizzes', 'rewards', 'profile'],
   *   onAction: (action) => console.log('Action:', action),
   *   onError: (error) => console.error('Error:', error)
   * });
   *
   * // Then render the UI
   * await client.render();
   * ```
   */
  configure(config: UIConfig): LuqtaClient {
    if (!config.containerId) {
      throw new LuqtaError('Container ID is required', 'MISSING_CONTAINER_ID');
    }

    // Switch to preconfigured mode
    this.mode = 'preconfigured';

    // Set UI configuration
    this.containerId = config.containerId;
    this.branding = config.branding;
    this.locale = config.locale ?? 'en';
    this.rtl = config.rtl ?? false;
    this.screens = config.screens ?? ['contests', 'quizzes', 'rewards', 'profile'];
    this.onAction = config.onAction;
    this.onError = config.onError;
    this.showProfileIcon = config.showProfileIcon ?? false;

    return this; // Enable method chaining
  }

  /**
   * Updates branding configuration
   * Can be called anytime to update the UI theme
   *
   * @param branding - Branding configuration options
   * @returns The client instance for method chaining
   *
   * @example
   * ```typescript
   * client.setBranding({
   *   primaryColor: '#ff5722',
   *   logoUrl: 'https://example.com/new-logo.png'
   * });
   * ```
   */
  setBranding(branding: BrandingConfig): LuqtaClient {
    this.branding = { ...this.branding, ...branding };
    return this;
  }

  /**
   * Gets current branding configuration
   */
  getBranding(): BrandingConfig | undefined {
    return this.branding;
  }

  /**
   * Sets the locale for the UI
   *
   * @param locale - Language code ('en' or 'ar')
   * @param rtl - Enable RTL layout (auto-detected for 'ar' if not specified)
   * @returns The client instance for method chaining
   */
  setLocale(locale: 'en' | 'ar', rtl?: boolean): LuqtaClient {
    this.locale = locale;
    this.rtl = rtl ?? (locale === 'ar');
    return this;
  }

  /**
   * Gets current locale
   */
  getLocale(): 'en' | 'ar' {
    return this.locale;
  }

  // ============================================================================
  // USER INITIALIZATION (End-User APIs)
  // ============================================================================

  /**
   * Initializes end-user session
   * Required before calling any end-user APIs
   *
   * Flow:
   * 1. Call /sdk/app/initialize/user with email or phone
   * 2. If returns SDK-USER-001 (user not synced), throw error asking to sync first
   * 3. On success, store token as userAuthorizationAccessTokenLuqtaSdk
   *
   * @throws {LuqtaError} If user config is missing or initialization fails
   */
  async initializeUser(): Promise<void> {
    // Security: Check rate limits and prevent duplicate requests
    this.checkRateLimit(USER_ENDPOINTS.INITIALIZE_USER);
    if (this.isDuplicateRequest(USER_ENDPOINTS.INITIALIZE_USER, null)) {
      throw new LuqtaError('Duplicate initialization request. Please wait.', 'DUPLICATE_REQUEST');
    }

    if (!this.user) {
      throw new LuqtaError(
        'User configuration is required for user initialization. Set user in config or call setUser() first.',
        'MISSING_USER_CONFIG'
      );
    }

    const initURL = `${this.baseURL}${USER_ENDPOINTS.INITIALIZE_USER}`;

    // Sanitize all identifiers
    const sanitizedEmail    = this.user.email?.trim().toLowerCase();
    const sanitizedPhone    = this.user.phone_number?.trim();
    const sanitizedUsername = this.user.username?.trim();
    const sanitizedUuid     = this.user.uuid?.trim();

    if (!sanitizedEmail && !sanitizedPhone && !sanitizedUsername && !sanitizedUuid) {
      throw new LuqtaError(
        'At least one identifier is required: email, phone_number, username, or uuid',
        'MISSING_USER_IDENTIFIER'
      );
    }

    // Validate email format if provided
    if (sanitizedEmail && !this.EMAIL_PATTERN.test(sanitizedEmail)) {
      throw new LuqtaError(
        'Invalid email format. Please provide a valid email address',
        'INVALID_EMAIL_FORMAT'
      );
    }

    // Validate phone format if provided
    if (sanitizedPhone && !this.PHONE_PATTERN.test(sanitizedPhone)) {
      throw new LuqtaError(
        'Invalid phone number format. Use international format (e.g., +923001234567)',
        'INVALID_PHONE_FORMAT'
      );
    }

    // Build X-User header — server handles all hashing in zero_storage mode
    const xUserPayload: Record<string, string | boolean> = {};
    if (sanitizedEmail)    xUserPayload.email        = sanitizedEmail;
    if (sanitizedPhone)    xUserPayload.phone_number = sanitizedPhone;
    if (sanitizedUsername) xUserPayload.username     = sanitizedUsername;
    if (sanitizedUuid)     xUserPayload.uuid         = sanitizedUuid;
    if (this.zeroStorage)  xUserPayload.zero_storage = true;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.apiKey}`,
      'X-App-Token': this.appId,
      'X-User': JSON.stringify(xUserPayload),
      'X-Data-Source': this.production ? 'live' : 'dev',
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(initURL, {
        method: 'POST',
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      // Check if user needs to be synced first
      if (data.code === 'SDK-USER-001') {
        throw new LuqtaError(
          'Please sync user first before initializing',
          'USER_NOT_SYNCED',
          undefined,
          { requiresSync: true }
        );
      }

      if (!response.ok || !data.success) {
        throw new LuqtaError(
          data.message || 'User initialization failed',
          data.code || 'USER_INIT_FAILED',
          undefined,
          data
        );
      }

      if (data.data?.authToken) {
        this.storeUserAuthToken(data.data.authToken);
        this.isUserInitialized = true;
      } else {
        throw new LuqtaError('No authentication token received', 'MISSING_AUTH_TOKEN');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.handleError(error, 'User initialization');
    }
  }

  /**
   * Alias for initializeUser() - for backward compatibility
   * @deprecated Use initializeUser() instead
   */
  async initialize(): Promise<void> {
    return this.initializeUser();
  }

  /**
   * Sets user identification for end-user APIs
   */
  setUser(user: UserIdentification): void {
    this.validateUserConfig(user);
    this.user = user;
    this.isUserInitialized = false; // Require re-initialization
  }

  /**
   * Checks if user is initialized
   */
  isInitialized(): boolean {
    return this.isUserInitialized;
  }

  /**
   * True when a user auth token is persisted in cookies (e.g. from a
   * previous session). Useful to decide whether to send user credentials
   * on endpoints that accept but don't require them, even when the
   * in-memory init flag hasn't been set yet.
   */
  hasUserToken(): boolean {
    return !!this.getUserAuthToken();
  }

  /**
   * Restores in-memory init flags from persisted tokens. Call this on
   * app boot — if a previous session saved tokens they can be reused
   * without re-running `initializeSdk()` / `initializeUser()`.
   * Returns whether a user token was found.
   */
  tryRestoreSession(): boolean {
    if (this.getSdkAuthToken()) {
      // SDK token presence is implicit — the app-level fetch path uses it
      // directly via buildAppHeaders when the auth token is present.
    }
    const hasUser = !!this.getUserAuthToken();
    if (hasUser) {
      this.isUserInitialized = true;
    }
    return hasUser;
  }

  /**
   * Syncs user profile data to Luqta
   * Call this before initializeUser() if user doesn't exist yet
   *
   * @param userData - User profile data including name, email, phone, etc.
   * @returns Promise with sync result
   *
   * @example
   * ```typescript
   * await client.syncUser({
   *   name: 'John Doe',
   *   email: 'john@example.com',
   *   phone_number: '+1234567890',
   *   gender: 'male',
   *   country: 'Pakistan',
   *   dob: null,
   *   Verified: true,
   *   policy_accept: true
   * });
   * ```
   */
  async syncUser(userData: UserProfile): Promise<ApiResponse> {
    // Security: Check rate limits and prevent duplicate requests
    this.checkRateLimit(USER_ENDPOINTS.SYNC_USER);
    if (this.isDuplicateRequest(USER_ENDPOINTS.SYNC_USER, userData)) {
      throw new LuqtaError('Duplicate sync request. Please wait.', 'DUPLICATE_REQUEST');
    }

    const syncURL = `${this.baseURL}${USER_ENDPOINTS.SYNC_USER}`;

    // Sanitize identifiers
    const sanitizedData = { ...userData };
    if (sanitizedData.email) {
      sanitizedData.email = sanitizedData.email.trim().toLowerCase();
      if (!this.EMAIL_PATTERN.test(sanitizedData.email)) {
        throw new LuqtaError(
          'Invalid email format. Please provide a valid email address',
          'INVALID_EMAIL_FORMAT'
        );
      }
    }
    if (sanitizedData.phone_number) {
      sanitizedData.phone_number = sanitizedData.phone_number.trim();
      if (!this.PHONE_PATTERN.test(sanitizedData.phone_number)) {
        throw new LuqtaError(
          'Invalid phone number format. Use international format (e.g., +923001234567)',
          'INVALID_PHONE_FORMAT'
        );
      }
    }

    // Require at least one identifier
    const hasIdentifier =
      !!sanitizedData.email ||
      !!sanitizedData.phone_number ||
      !!sanitizedData.username ||
      !!sanitizedData.uuid ||
      !!sanitizedData.user_id ||
      !!sanitizedData.sdk_user_id;

    if (!hasIdentifier) {
      throw new LuqtaError(
        'At least one identifier is required: email, phone_number, username, or uuid',
        'MISSING_USER_INFO'
      );
    }

    // Server handles all hashing in zero_storage mode
    const syncBody: Record<string, any> = { ...sanitizedData };
    if (this.zeroStorage) {
      syncBody.zero_storage = true;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.apiKey}`,
      'X-App-Token': this.appId,
      'X-Data-Source': this.production ? 'live' : 'dev',
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(syncURL, {
        method: 'POST',
        headers,
        body: JSON.stringify(syncBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.parseErrorResponse(response, 'User sync failed', 'USER_SYNC_FAILED');
      }

      const data = await response.json();

      if (!data.success) {
        throw new LuqtaError(
          data.message || 'User sync failed',
          data.code || 'USER_SYNC_FAILED',
          undefined,
          data
        );
      }

      // Update user identification with sanitized data
      if (sanitizedData.email)        this.user = { ...this.user, email:        sanitizedData.email };
      if (sanitizedData.phone_number) this.user = { ...this.user, phone_number: sanitizedData.phone_number };
      if (sanitizedData.username)     this.user = { ...this.user, username:     sanitizedData.username };
      if (sanitizedData.uuid)         this.user = { ...this.user, uuid:         sanitizedData.uuid };

      return { success: true, data };
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.handleError(error, 'User sync');
    }
  }

  /**
   * Syncs user and then initializes - convenience method
   * Use this when you need to sync a new user and get auth token
   *
   * @param userData - User profile data
   * @returns Promise resolving when user is synced and initialized
   */
  async syncAndInitializeUser(userData: UserProfile): Promise<void> {
    // First sync the user
    await this.syncUser(userData);

    // Then initialize to get auth token
    await this.initializeUser();
  }

  // ============================================================================
  // APPLICATION-LEVEL APIs (No user required)
  // ============================================================================

  /**
   * Gets all public contests (no user required)
   */
  async getPublicContests(params?: { page?: number; per_page?: number }): Promise<ApiResponse> {
    return this.applicationRequest(APPLICATION_ENDPOINTS.GET_ALL_CONTESTS, { params });
  }

  /**
   * Gets trending contests (no user required)
   */
  async getTrendingContests(): Promise<ApiResponse<Contest[]>> {
    return this.applicationRequest(APPLICATION_ENDPOINTS.GET_TRENDING_CONTESTS);
  }

  /**
   * Gets premium/VIP contests (no user required)
   */
  async getPremiumContests(): Promise<ApiResponse<Contest[]>> {
    return this.applicationRequest(APPLICATION_ENDPOINTS.GET_PREMIUM_CONTESTS);
  }

  /**
   * Gets recent contests (no user required)
   */
  async getRecentContests(): Promise<ApiResponse<Contest[]>> {
    return this.applicationRequest(APPLICATION_ENDPOINTS.GET_RECENT_CONTESTS);
  }

  /**
   * Gets contest details with optional user progress
   * Uses user token if user is initialized (to get progress), otherwise uses SDK token
   *
   * @param contestId - The contest ID to get details for
   * @param accessCode - Access code for private contests (optional)
   * @returns Contest details with levels, prizes, and user progress (if user is initialized)
   */
  async getContestDetails(contestId: number, accessCode?: string): Promise<ApiResponse> {
    // Security: Validate contest ID parameter
    const validatedContestId = this.validateNumericParam(contestId, 'contestId');

    // Security: Check rate limits
    this.checkRateLimit('/sdk/app/contest/contest-details');

    // Security: Validate access code if provided
    if (accessCode !== undefined && accessCode !== null) {
      accessCode = this.validateStringParam(accessCode, 'accessCode', 100);
    }

    if (!this.isSdkInitialized) {
      throw new LuqtaError(
        'SDK must be initialized before fetching contest details. Call initializeSdk() first.',
        'SDK_NOT_INITIALIZED'
      );
    }

    const queryParams = new URLSearchParams();
    queryParams.append('contest_id', String(validatedContestId));
    if (accessCode) {
      queryParams.append('access_code', accessCode);
    }

    const url = `${this.baseURL}/sdk/app/contest/contest-details?${queryParams.toString()}`;

    // Get both tokens
    const userToken = this.getUserAuthToken();
    const sdkToken = this.getSdkAuthToken();

    if (!sdkToken) {
      throw new LuqtaError(
        'SDK authentication token not found. Please re-initialize the SDK.',
        'MISSING_AUTH_TOKEN'
      );
    }

    // Build headers - always include SDK token, add user token if available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sdkToken}`,
      'X-App-Token': this.appId,
      'X-Data-Source': this.production ? 'live' : 'dev',
    };

    // Add user token header if user is initialized (to get progress data)
    if (this.isUserInitialized && userToken) {
      headers['AuthorizationUser'] = `Bearer ${userToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.parseErrorResponse(response, 'Failed to fetch contest details', 'FETCH_CONTEST_DETAILS_FAILED');
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.handleError(error, 'Fetch contest details');
    }
  }

  /**
   * Gets categories (no user required)
   */
  async getCategories(): Promise<ApiResponse> {
    return this.applicationRequest(APPLICATION_ENDPOINTS.GET_CATEGORIES);
  }

  /**
   * Gets available rewards (no user required)
   */
  async getRewardsList(): Promise<ApiResponse> {
    return this.applicationRequest(APPLICATION_ENDPOINTS.GET_REWARDS);
  }

  // ============================================================================
  // CONTEST PARTICIPATION (User required)
  // ============================================================================

  /**
   * Gets contest details with user progress
   * Requires user to be initialized with auth token
   *
   * @param contestId - The contest ID to get details for
   * @returns Contest details with progress
   * @throws {LuqtaError} If user needs to participate first (ERROR_FATAL)
   */
  async getContestDetailsProgress(contestId: number): Promise<ApiResponse> {
    if (!this.isUserInitialized) {
      throw new LuqtaError(
        'User must be initialized before getting contest details. Call initializeUser() first.',
        'USER_NOT_INITIALIZED'
      );
    }

    return this.userRequest(USER_ENDPOINTS.GET_CONTEST_PROGRESS, {
      method: 'GET',
      params: { contest_id: contestId }
    });
  }

  /**
   * Participates in a contest
   * Requires user to be initialized with auth token
   *
   * @param contestId - The contest ID to participate in
   * @param accessCode - Access code for private contests (optional)
   * @returns Participation result
   */
  async participateContest(contestId: number, accessCode?: string): Promise<ApiResponse> {
    if (!this.isUserInitialized) {
      throw new LuqtaError(
        'User must be initialized before participating. Call initializeUser() first.',
        'USER_NOT_INITIALIZED'
      );
    }

    const body: { contest_id: number; access_code?: string } = {
      contest_id: contestId
    };

    if (accessCode) {
      body.access_code = accessCode;
    }

    return this.userRequest(USER_ENDPOINTS.PARTICIPATE, {
      method: 'POST',
      body
    });
  }

  // ============================================================================
  // PRE-CONFIGURED MODE
  // ============================================================================

  /**
   * Renders the pre-configured UI
   * Only available in preconfigured mode
   *
   * Flow:
   * 1. Initialize SDK (apiKey + appId only, no user required)
   * 2. Fetch contests
   * 3. Render UI with branding config
   *
   * @example
   * ```typescript
   * const client = new LuqtaClient({
   *   mode: 'preconfigured',
   *   apiKey: 'your-api-key',
   *   appId: 'your-app-id',
   *   containerId: 'luqta-container',
   *   branding: { primaryColor: '#5304fb' }
   * });
   *
   * await client.render();
   * ```
   */
  async render(): Promise<void> {
    if (this.mode !== 'preconfigured') {
      throw new LuqtaError(
        'render() is only available in preconfigured mode',
        'INVALID_MODE'
      );
    }

    if (!this.containerId) {
      throw new LuqtaError('Container ID is required', 'MISSING_CONTAINER_ID');
    }

    // Step 1: Initialize SDK first (only apiKey + appId required)
    let initialContests: Contest[] = [];
    try {
      const initResult = await this.initializeSdk();
      initialContests = initResult.contests.data?.items || [];
    } catch (error) {
      // SDK initialization failed
      throw error;
    }

    // Step 2: Initialize user if user config provided (optional).
    // If init fails with SDK-USER-001 (sync required) and a userProfile was
    // supplied, automatically sync + initialize.
    if (this.user && !this.isUserInitialized) {
      try {
        await this.initializeUser();
      } catch (error: any) {
        const msg = (error?.message ?? '').toLowerCase();
        const needsSync = msg.includes('sync user first') ||
          msg.includes('sdk-user-001') ||
          msg.includes('please sync');
        if (needsSync && this.userProfile) {
          try {
            await this.syncAndInitializeUser(this.userProfile);
          } catch (syncErr) {
            console.warn('User auto-sync failed, continuing with app-level APIs:', syncErr);
          }
        } else {
          console.warn('User initialization failed, continuing with app-level APIs:', error);
        }
      }
    }

    // Step 3: Create UI renderer with branding config
    this.uiRenderer = new LuqtaUIRenderer(this, {
      containerId: this.containerId,
      branding: this.branding,
      locale: this.locale,
      rtl: this.rtl,
      showProfileIcon: this.showProfileIcon,
      onAction: this.onAction,
      onError: this.onError,
    });

    // Step 4: Render the UI with initial contests
    await this.uiRenderer.renderWithContests(initialContests);
  }

  /**
   * Refreshes the UI content
   * Only available in preconfigured mode after render() has been called
   */
  async refreshUI(): Promise<void> {
    if (!this.uiRenderer) {
      throw new LuqtaError(
        'UI has not been rendered yet. Call render() first.',
        'UI_NOT_RENDERED'
      );
    }

    await this.uiRenderer.refresh();
  }

  // ============================================================================
  // HTTP REQUEST METHODS
  // ============================================================================

  /**
   * Makes an application-level request (no user auth required)
   * Use for: contest listings, categories, public rewards
   */
  async applicationRequest<T = any>(
    endpoint: string,
    options: RequestOptions = {},
    _isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    // Security: Perform all security checks before making request
    this.performSecurityChecks(endpoint, options);

    const url = this.buildRequestURL(endpoint, options.params);
    const headers = this.buildAppHeaders(options.headers);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.parseErrorResponse(response, `Request to ${endpoint} failed`, 'REQUEST_FAILED');
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      clearTimeout(timeoutId);
      // One-shot auto-retry when the backend signals an expired token.
      if (!_isRetry && error instanceof LuqtaError && this.isTokenExpired(error)) {
        const refreshed = await this.refreshSdkToken();
        if (refreshed) {
          return this.applicationRequest<T>(endpoint, options, true);
        }
      }
      throw this.handleError(error, 'Request');
    }
  }

  /**
   * Makes an end-user request (requires user initialization)
   * Use for: participation, quiz, rewards, profile, etc.
   */
  async userRequest<T = any>(
    endpoint: string,
    options: RequestOptions = {},
    _isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    // Security: Perform all security checks before making request
    this.performSecurityChecks(endpoint, options);

    // Allow the call as long as a user token is available — either from
    // this session (`isUserInitialized`) or persisted from a previous
    // session. `buildUserHeaders` still throws when no token is found.
    if (!this.isUserInitialized && !this.hasUserToken()) {
      throw new LuqtaError(
        'User must be initialized before making user requests. Call initializeUser() first.',
        'USER_NOT_INITIALIZED'
      );
    }

    const url = this.buildRequestURL(endpoint, options.params);
    const headers = this.buildUserHeaders(options.headers);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.parseErrorResponse(response, `Request to ${endpoint} failed`, 'REQUEST_FAILED');
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      clearTimeout(timeoutId);
      // One-shot auto-retry when the backend signals an expired token.
      if (!_isRetry && error instanceof LuqtaError && this.isTokenExpired(error)) {
        const refreshed = await this.refreshUserToken();
        if (refreshed) {
          return this.userRequest<T>(endpoint, options, true);
        }
      }
      throw this.handleError(error, 'Request');
    }
  }

  /**
   * Detects the `{code: ERROR_FATAL, message: "The token expired."}` pattern
   * that the backend emits on stale bearer tokens.
   */
  private isTokenExpired(err: LuqtaError): boolean {
    if (err.code !== 'ERROR_FATAL') return false;
    const msg = (err.message || '').toLowerCase();
    return msg.includes('token') && msg.includes('expired');
  }

  /** Refreshes the user auth token. On sync-required errors, auto-syncs if
   * `userProfile` was provided in config. Returns true on success. */
  private async refreshUserToken(): Promise<boolean> {
    try {
      await this.initializeUser();
      return this.isUserInitialized;
    } catch (error: any) {
      const msg = (error?.message ?? '').toLowerCase();
      const needsSync = msg.includes('sync user first') ||
        msg.includes('sdk-user-001') ||
        msg.includes('please sync');
      if (needsSync && this.userProfile) {
        try {
          await this.syncAndInitializeUser(this.userProfile);
          return this.isUserInitialized;
        } catch {
          return false;
        }
      }
      return false;
    }
  }

  /** Refreshes the SDK auth token by re-running initializeSdk(). */
  private async refreshSdkToken(): Promise<boolean> {
    try {
      await this.initializeSdk();
      return !!this.getSdkAuthToken();
    } catch {
      return false;
    }
  }

  /**
   * Generic request method (for backward compatibility)
   * Uses user request if initialized, otherwise application request
   */
  async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    // Prefer user-auth whenever a user token is available — even when the
    // in-memory init flag isn't set (e.g. returning user, token in cookie
    // from a previous session).
    if (this.isUserInitialized || this.hasUserToken()) {
      return this.userRequest<T>(endpoint, options);
    }
    return this.applicationRequest<T>(endpoint, options);
  }

  // HTTP method shortcuts
  async get<T = any>(endpoint: string, params?: Record<string, string | number | boolean>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  async put<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async patch<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  // ============================================================================
  // INTERNAL HELPERS
  // ============================================================================

  private buildRequestURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
    // Properly concatenate baseURL with endpoint
    // baseURL: https://xi5f-5y62-kbyr.n7e.xano.io/api:ev5HkJDh:dev
    // endpoint: /sdk/app/contest/all
    // result: https://xi5f-5y62-kbyr.n7e.xano.io/api:ev5HkJDh:dev/sdk/app/contest/all
    const baseWithoutTrailingSlash = this.baseURL.replace(/\/$/, '');
    const endpointWithLeadingSlash = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    let fullUrl = `${baseWithoutTrailingSlash}${endpointWithLeadingSlash}`;

    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      fullUrl += `?${searchParams.toString()}`;
    }

    return fullUrl;
  }

  /**
   * Builds headers for application-level requests (API Key + App ID only)
   */
  private buildAppHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    return {
      ...this.defaultHeaders,
      'Authorization': `Bearer ${this.apiKey}`,
      'X-App-Token': this.appId,
      'X-Data-Source': this.production ? 'live' : 'dev',
      ...customHeaders
    };
  }

  /**
   * Builds headers for initialization (API Key + App ID + User)
   */
  private buildInitHeaders(): Record<string, string> {
    return {
      ...this.defaultHeaders,
      'Authorization': `Bearer ${this.apiKey}`,
      'X-App-Token': this.appId,
      'X-User': JSON.stringify(this.user),
      'X-Data-Source': this.production ? 'live' : 'dev'
    };
  }

  /**
   * Builds headers for end-user requests (User Auth Token)
   */
  private buildUserHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    const userToken = this.getUserAuthToken();
    if (!userToken) {
      throw new LuqtaError(
        'User authentication token not found. Please initialize user first.',
        'MISSING_USER_TOKEN'
      );
    }
    return {
      ...this.defaultHeaders,
      'Authorization': `Bearer ${userToken}`,
      'X-Data-Source': this.production ? 'live' : 'dev',
      ...customHeaders
    };
  }

  private storeSdkAuthToken(token: string): void {
    if (typeof document !== 'undefined') {
      document.cookie = `${this.SDK_AUTH_TOKEN_KEY}=${token}; path=/; SameSite=Strict`;
    }
  }

  private getSdkAuthToken(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie.match(new RegExp(`${this.SDK_AUTH_TOKEN_KEY}=([^;]+)`));
    return match?.[1];
  }

  private storeUserAuthToken(token: string): void {
    if (typeof document !== 'undefined') {
      document.cookie = `${this.USER_AUTH_TOKEN_KEY}=${token}; path=/; SameSite=Strict`;
    }
  }

  private getUserAuthToken(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie.match(new RegExp(`${this.USER_AUTH_TOKEN_KEY}=([^;]+)`));
    return match?.[1];
  }

  private async parseErrorResponse(response: Response, defaultMessage: string, defaultCode: string): Promise<LuqtaError> {
    try {
      const data = await response.json();
      return new LuqtaError(
        data.message || data.error?.message || defaultMessage,
        data.code || data.error?.code || defaultCode,
        response.status,
        data
      );
    } catch {
      return new LuqtaError(response.statusText || defaultMessage, defaultCode, response.status);
    }
  }

  private handleError(error: unknown, context: string): LuqtaError {
    if (error instanceof LuqtaError) return error;

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return new LuqtaError(`${context} request timed out`, 'TIMEOUT');
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return new LuqtaError('Network error occurred', 'NETWORK_ERROR');
      }
      return new LuqtaError(`${context} failed: ${error.message}`, 'REQUEST_FAILED');
    }

    return new LuqtaError(`Unexpected error during ${context.toLowerCase()}`, 'UNKNOWN_ERROR');
  }

  // ============================================================================
  // CONFIGURATION SETTERS/GETTERS
  // ============================================================================

  setApiKey(apiKey: string): void {
    if (!apiKey) throw new LuqtaError('API key cannot be empty', 'INVALID_API_KEY');
    this.apiKey = apiKey;
  }

  getApiKey(): string {
    return this.apiKey.substring(0, 8) + '...';
  }

  setAppId(appId: string): void {
    if (!appId) throw new LuqtaError('App ID cannot be empty', 'INVALID_APP_ID');
    this.appId = appId;
  }

  getAppId(): string {
    return this.appId;
  }

  setProduction(production: boolean): void {
    this.production = production;
    this.baseURL = this.constructBaseURL(production);
  }

  isProductionMode(): boolean {
    return this.production;
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  setTimeout(timeout: number): void {
    if (timeout <= 0) throw new LuqtaError('Timeout must be greater than 0', 'INVALID_TIMEOUT');
    this.timeout = timeout;
  }

  getTimeout(): number {
    return this.timeout;
  }

  getMode(): 'custom' | 'preconfigured' {
    return this.mode;
  }
}

// ============================================================================
// SUB-API CLASSES
// ============================================================================

/**
 * Contest API methods
 */
class ContestAPI {
  constructor(private client: LuqtaClient) {}

  /** Get all contests (Application API - no user required) */
  async getAll(params?: { page?: number; per_page?: number }) {
    return this.client.applicationRequest(APPLICATION_ENDPOINTS.GET_ALL_CONTESTS, { params });
  }

  /** Get trending contests (Application API - no user required) */
  async getTrending() {
    return this.client.applicationRequest(APPLICATION_ENDPOINTS.GET_TRENDING_CONTESTS);
  }

  /** Get premium contests (Application API - no user required) */
  async getPremium() {
    return this.client.applicationRequest(APPLICATION_ENDPOINTS.GET_PREMIUM_CONTESTS);
  }

  /** Get recent contests (Application API - no user required) */
  async getRecent() {
    return this.client.applicationRequest(APPLICATION_ENDPOINTS.GET_RECENT_CONTESTS);
  }

  /** Participate in a contest (End-User API - requires initialization) */
  async participate(contestId: number, accessCode?: string) {
    return this.client.userRequest(USER_ENDPOINTS.PARTICIPATE, {
      method: 'POST',
      body: { contest_id: contestId, access_code: accessCode }
    });
  }

  /** Get contest progress (End-User API - requires initialization) */
  async getProgress(contestId: number) {
    return this.client.userRequest(USER_ENDPOINTS.GET_CONTEST_PROGRESS, {
      params: { contest_id: contestId }
    });
  }

  /** Get contest history (End-User API - requires initialization) */
  async getHistory() {
    return this.client.userRequest(USER_ENDPOINTS.GET_CONTEST_HISTORY);
  }

  /** Get contest compete data with levels progress (End-User API - requires initialization) */
  async compete(contestId: number) {
    return this.client.userRequest('/sdk/app/contest/compete', {
      params: { contest_id: contestId }
    });
  }
}

/**
 * Level API methods
 */
class LevelAPI {
  constructor(private client: LuqtaClient) {}

  /** Complete a level (End-User API) */
  async complete(levelId: number, data: { textContent?: string; qrCode?: string; link?: string }) {
    return this.client.userRequest(USER_ENDPOINTS.COMPLETE_LEVEL, {
      method: 'POST',
      body: { level_id: levelId, ...data }
    });
  }

  /** Complete level with image (End-User API) */
  async completeWithImage(levelId: number, imageUrl: string) {
    return this.client.userRequest(USER_ENDPOINTS.COMPLETE_LEVEL_IMAGE, {
      method: 'POST',
      body: { level_id: levelId, image_url: imageUrl }
    });
  }

  /**
   * Complete a `client_webhook` level via the dedicated
   * `/sdk/app/client-webhook` endpoint (End-User API).
   *
   * `variables` is one entry per `Level.client_variables` item, each shaped
   * as `{ variable_name, data_type, value }`.
   */
  async completeClientWebhook(
    levelId: number,
    variables: Array<{ variable_name: string; data_type: string; value: unknown }>
  ) {
    return this.client.userRequest(USER_ENDPOINTS.COMPLETE_CLIENT_WEBHOOK, {
      method: 'POST',
      body: { level_id: levelId, variables }
    });
  }

  /** Update level progress (End-User API) */
  async updateProgress(levelId: number) {
    return this.client.userRequest(USER_ENDPOINTS.UPDATE_LEVEL_PROGRESS, {
      method: 'POST',
      body: { level_id: levelId }
    });
  }

  /** Get congratulation screen data (End-User API) */
  async getCongratulation(levelId: number, contestId: number) {
    return this.client.userRequest(USER_ENDPOINTS.GET_LEVEL_CONGRATULATION, {
      params: { level_id: levelId, contest_id: contestId }
    });
  }

  /** Scan QR code (End-User API) */
  async scanQR(qrData: string) {
    return this.client.userRequest(USER_ENDPOINTS.SCAN_QR, {
      method: 'POST',
      body: { qr_data: qrData }
    });
  }
}

/**
 * Quiz API methods
 */
class QuizAPI {
  constructor(private client: LuqtaClient) {}

  /** Start a quiz (End-User API) */
  async start(quizId: number, levelId: number) {
    return this.client.userRequest(USER_ENDPOINTS.START_QUIZ, {
      params: { quiz_id: quizId, level_id: levelId }
    });
  }

  /** Submit an answer (End-User API) */
  async submitAnswer(attemptId: number, questionId: number, optionId: number, levelId: number) {
    return this.client.userRequest(USER_ENDPOINTS.SUBMIT_ANSWER, {
      method: 'POST',
      body: { current_attempt_id: attemptId, current_question_id: questionId, answer_id: optionId, level_id: levelId }
    });
  }

  /** Submit/complete quiz (End-User API) */
  async submit(attemptId: number) {
    return this.client.userRequest(USER_ENDPOINTS.SUBMIT_QUIZ, {
      method: 'POST',
      body: { attempt_id: attemptId }
    });
  }
}

/**
 * Reward API methods
 */
class RewardAPI {
  constructor(private client: LuqtaClient) {}

  /** Get rewards list (Application API - no user required) */
  async getList() {
    return this.client.applicationRequest(APPLICATION_ENDPOINTS.GET_REWARDS);
  }

  /** Get user earnings (End-User API) */
  async getEarnings() {
    return this.client.userRequest(USER_ENDPOINTS.GET_EARNINGS);
  }

  /** Redeem a reward (End-User API) */
  async redeem(rewardId: number, points: number) {
    return this.client.userRequest(USER_ENDPOINTS.REDEEM_REWARD, {
      method: 'POST',
      body: { reward_id: rewardId, points }
    });
  }

  /** Get reward history (End-User API) */
  async getHistory() {
    return this.client.userRequest(USER_ENDPOINTS.GET_REWARD_HISTORY);
  }

  /** Get prize history (End-User API) */
  async getPrizeHistory() {
    return this.client.userRequest(USER_ENDPOINTS.GET_PRIZE_HISTORY);
  }
}

/**
 * Notification API methods
 */
class NotificationAPI {
  constructor(private client: LuqtaClient) {}

  /** Get all notifications (End-User API) */
  async getAll() {
    return this.client.userRequest(USER_ENDPOINTS.GET_NOTIFICATIONS);
  }

  /** Mark notifications as read (End-User API) */
  async markAsRead(notificationIds: number[]) {
    return this.client.userRequest(USER_ENDPOINTS.MARK_NOTIFICATIONS_READ, {
      method: 'POST',
      body: { notification_ids: notificationIds }
    });
  }

  /** Update notification settings (End-User API) */
  async updateSettings(settings: { push_enabled?: boolean; email_enabled?: boolean }) {
    return this.client.userRequest(USER_ENDPOINTS.UPDATE_NOTIFICATION_SETTINGS, {
      method: 'POST',
      body: settings
    });
  }
}

/**
 * Profile API methods
 */
class ProfileAPI {
  constructor(private client: LuqtaClient) {}

  /** Get user profile (End-User API) */
  async get() {
    return this.client.userRequest(USER_ENDPOINTS.GET_PROFILE);
  }

  /** Get user activities (End-User API) */
  async getActivities() {
    return this.client.userRequest(USER_ENDPOINTS.GET_USER_ACTIVITIES);
  }

  /** Get user progress (End-User API) */
  async getProgress() {
    return this.client.userRequest(USER_ENDPOINTS.GET_USER_PROGRESS);
  }

  /** Submit app feedback (End-User API) */
  async submitFeedback(rating: number, feedback: string) {
    return this.client.userRequest(USER_ENDPOINTS.SUBMIT_FEEDBACK, {
      method: 'POST',
      body: { rating, feedback }
    });
  }

  /** Delete user account (End-User API) */
  async deleteAccount() {
    return this.client.userRequest(USER_ENDPOINTS.DELETE_ACCOUNT);
  }
}
